import { useEffect, useState, useCallback, useRef } from "react";
import { Plus, Pencil, Trash2, GripVertical, Tags, ArrowRightLeft, Phone, MessageCircle, Search, StickyNote, CalendarClock, MessagesSquare, AlarmClock, Upload, FileDown } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const todayStr = () => new Date().toLocaleDateString("en-CA");

export default function LeadsCRM() {
  const [labels, setLabels] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [managerOpen, setManagerOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [dragLeadId, setDragLeadId] = useState(null);
  const dragLeadRef = useRef(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [noteLead, setNoteLead] = useState(null);

  const load = useCallback(async () => {
    try {
      const [lbl, lds] = await Promise.all([api.get("/admin/lead-labels"), api.get("/admin/leads")]);
      setLabels(lbl.data);
      setLeads(lds.data);
    } catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const moveLeadId = async (leadId, labelId) => {
    const lead = leads.find((l) => l.id === leadId);
    if (!lead || lead.label_id === labelId) return;
    setLeads((cur) => cur.map((l) => (l.id === leadId ? { ...l, label_id: labelId } : l)));
    try { await api.patch(`/admin/leads/${leadId}/move`, { label_id: labelId }); toast.success("Lead moved"); }
    catch (e) { toast.error(apiError(e)); load(); }
  };

  const deleteLead = async (lead) => {
    if (!window.confirm(`Remove ${lead.company_name || lead.mobile} from the leads board?`)) return;
    try { await api.delete(`/admin/leads/${lead.id}`); setLeads((c) => c.filter((l) => l.id !== lead.id)); }
    catch (e) { toast.error(apiError(e)); }
  };

  const templateFor = (labelId) => labels.find((l) => l.id === labelId)?.message || "";
  const fillTemplate = (tpl, lead) => (tpl || "Hi {company}, this is the Cleartile team.")
    .replaceAll("{company}", lead.company_name && lead.company_name !== "—" ? lead.company_name : "there")
    .replaceAll("{mobile}", lead.mobile || "")
    .replaceAll("{city}", lead.city || "");
  const waLink = (lead) => {
    const num = String(lead.whatsapp || lead.mobile || "").replace(/\D/g, "").slice(-10);
    return `https://wa.me/91${num}?text=${encodeURIComponent(fillTemplate(templateFor(lead.label_id), lead))}`;
  };

  const jumpToCard = (id) => {
    setQuery(""); setRoleFilter("all");
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="lead-card-${id}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
        el.classList.add("ring-2", "ring-primary", "ring-offset-2");
        setTimeout(() => el.classList.remove("ring-2", "ring-primary", "ring-offset-2"), 2200);
      }
    }, 120);
  };

  const q = query.trim().toLowerCase();
  const matches = (l) => {
    if (roleFilter !== "all" && l.role !== roleFilter) return false;
    if (!q) return true;
    return [l.company_name, l.mobile, l.city, l.whatsapp].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
  };
  const filtered = leads.filter(matches);
  const byLabel = (id) => filtered.filter((l) => l.label_id === id);

  const today = todayStr();
  const dueLeads = leads.filter((l) => l.follow_up && l.follow_up <= today).sort((a, b) => (a.follow_up < b.follow_up ? -1 : 1));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold flex items-center gap-2"><ArrowRightLeft className="h-6 w-6 text-primary" /> Leads CRM</h1>
          <p className="text-muted-foreground text-sm font-medium">Sellers auto-enter as <b>New Lead</b>, move to <b>Interested</b> on password change, and to <b>Converted</b> on first subscription payment. Drag cards between stages or use the dropdown.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="add-lead-btn" onClick={() => setAddOpen(true)} className="font-bold gap-1"><Plus className="h-4 w-4" /> Add Lead</Button>
          <Button data-testid="bulk-leads-btn" onClick={() => setBulkOpen(true)} variant="outline" className="font-bold gap-1"><Upload className="h-4 w-4" /> Upload CSV</Button>
          <Button data-testid="message-templates-btn" onClick={() => setTemplatesOpen(true)} variant="outline" className="font-bold gap-1"><MessagesSquare className="h-4 w-4" /> Message Templates</Button>
          <Button data-testid="manage-labels-btn" onClick={() => setManagerOpen(true)} variant="outline" className="font-bold gap-1"><Tags className="h-4 w-4" /> Manage Labels</Button>
        </div>
      </div>

      {dueLeads.length > 0 && (
        <div data-testid="followups-banner" className="bg-amber-50 border border-amber-300 rounded-2xl p-4">
          <div className="flex items-center gap-2 font-heading font-extrabold text-amber-900">
            <AlarmClock className="h-5 w-5" /> Follow-ups due today
            <Badge className="bg-amber-600 text-white border-0" data-testid="followups-count">{dueLeads.length}</Badge>
          </div>
          <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {dueLeads.map((lead) => {
              const overdue = lead.follow_up < today;
              return (
                <div key={lead.id} data-testid={`followup-item-${lead.id}`} className="bg-white border border-amber-200 rounded-xl px-3 py-2 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-bold text-sm truncate">{lead.company_name && lead.company_name !== "—" ? lead.company_name : lead.mobile}</div>
                    <div className={`text-xs font-semibold ${overdue ? "text-rose-600" : "text-amber-700"}`}>{overdue ? "Overdue · " : "Today · "}{lead.follow_up}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <a data-testid={`followup-whatsapp-${lead.id}`} href={waLink(lead)} target="_blank" rel="noreferrer" className="grid place-items-center h-8 w-8 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600"><MessageCircle className="h-4 w-4" /></a>
                    <button data-testid={`followup-jump-${lead.id}`} onClick={() => jumpToCard(lead.id)} className="text-xs font-bold text-primary underline px-1">View</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="lead-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by mobile, company or city…" className="pl-9 h-10" />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger data-testid="lead-role-filter" className="h-10 w-40 font-semibold"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All seller types</SelectItem>
            <SelectItem value="wholesaler">Wholesalers</SelectItem>
            <SelectItem value="retailer">Retailers</SelectItem>
          </SelectContent>
        </Select>
        <Badge variant="secondary" className="font-bold h-10 px-3 grid place-items-center" data-testid="lead-total-count">{filtered.length} leads</Badge>
      </div>

      {loading ? (
        <div className="text-center text-muted-foreground py-20">Loading leads…</div>
      ) : (
        <div className="flex gap-4 overflow-x-auto ct-scroll pb-4" data-testid="leads-board">
          {labels.map((label) => {
            const items = byLabel(label.id);
            const over = dragOverCol === label.id;
            return (
              <div key={label.id} data-testid={`lead-column-${label.id}`}
                onDragOver={(e) => { e.preventDefault(); if (dragLeadRef.current) setDragOverCol(label.id); }}
                onDragLeave={() => setDragOverCol((c) => (c === label.id ? null : c))}
                onDrop={() => { const id = dragLeadRef.current; if (id) moveLeadId(id, label.id); dragLeadRef.current = null; setDragLeadId(null); setDragOverCol(null); }}
                className={`w-72 shrink-0 rounded-2xl border transition-colors ${over ? "bg-primary/10 border-primary border-dashed" : "bg-secondary/50"}`}>
                <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderTopColor: label.color, borderTopWidth: 3 }}>
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full" style={{ background: label.color }} />
                    <span className="font-heading font-extrabold text-sm">{label.name}</span>
                  </div>
                  <Badge variant="secondary" className="font-bold" data-testid={`lead-count-${label.id}`}>{items.length}</Badge>
                </div>
                <div className="p-3 space-y-3 min-h-[120px]">
                  {items.map((lead) => (
                    <div key={lead.id} data-testid={`lead-card-${lead.id}`}
                      draggable onDragStart={() => { dragLeadRef.current = lead.id; setDragLeadId(lead.id); }} onDragEnd={() => { dragLeadRef.current = null; setDragLeadId(null); setDragOverCol(null); }}
                      className={`bg-card border rounded-xl p-3 shadow-sm cursor-grab active:cursor-grabbing ${dragLeadId === lead.id ? "opacity-40" : ""}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex items-start gap-1.5">
                          <GripVertical className="h-4 w-4 text-muted-foreground/50 shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <div className="font-bold text-sm truncate flex items-center gap-1.5">
                              {lead.serial != null && <span data-testid={`lead-serial-${lead.id}`} className="shrink-0 text-[10px] font-extrabold text-primary bg-primary/10 rounded px-1.5 py-0.5">#{lead.serial}</span>}
                              <span className="truncate">{lead.company_name && lead.company_name !== "—" ? lead.company_name : "New seller"}</span>
                            </div>
                            <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Phone className="h-3 w-3" /> {lead.mobile}{lead.city ? ` · ${lead.city}` : ""}</div>
                          </div>
                        </div>
                        <button data-testid={`delete-lead-${lead.id}`} onClick={() => deleteLead(lead)} className="text-muted-foreground hover:text-rose-600 shrink-0"><Trash2 className="h-4 w-4" /></button>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        <Badge className="capitalize bg-slate-100 text-slate-700 border-slate-300 border text-[10px]">{lead.role}</Badge>
                        {lead.profile_complete && <Badge className="bg-blue-100 text-blue-700 border-blue-300 border text-[10px]">Profile done</Badge>}
                        {lead.has_subscription && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 border text-[10px]">Subscribed</Badge>}
                        {lead.follow_up && <Badge data-testid={`lead-followup-${lead.id}`} className="bg-amber-100 text-amber-800 border-amber-300 border text-[10px] gap-1"><CalendarClock className="h-3 w-3" /> {lead.follow_up}</Badge>}
                      </div>
                      {lead.note && <div data-testid={`lead-note-preview-${lead.id}`} className="mt-2 text-xs bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5 text-amber-900 line-clamp-2">{lead.note}</div>}
                      <div className="flex items-center gap-1.5 mt-3">
                        <a data-testid={`lead-whatsapp-${lead.id}`} href={waLink(lead)} target="_blank" rel="noreferrer"
                          className="grid place-items-center h-8 w-8 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 shrink-0"><MessageCircle className="h-4 w-4" /></a>
                        <button data-testid={`lead-note-btn-${lead.id}`} onClick={() => setNoteLead(lead)}
                          className={`grid place-items-center h-8 w-8 rounded-lg border shrink-0 ${lead.note || lead.follow_up ? "bg-amber-100 border-amber-300 text-amber-700" : "hover:bg-secondary text-muted-foreground"}`}><StickyNote className="h-4 w-4" /></button>
                        <Select value={lead.label_id} onValueChange={(v) => moveLeadId(lead.id, v)}>
                          <SelectTrigger data-testid={`move-lead-${lead.id}`} className="h-8 text-xs font-semibold flex-1"><SelectValue placeholder="Move to…" /></SelectTrigger>
                          <SelectContent>
                            {labels.map((l) => <SelectItem key={l.id} value={l.id} className="text-xs">{l.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && <div className="text-center text-xs text-muted-foreground py-6 font-medium">{over ? "Drop here" : "No leads"}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <LabelManager open={managerOpen} onClose={() => setManagerOpen(false)} labels={labels} onChanged={load} />
      <TemplatePlanner open={templatesOpen} onClose={() => setTemplatesOpen(false)} labels={labels} onChanged={load} />
      <AddLeadDialog open={addOpen} onClose={() => setAddOpen(false)} labels={labels} onAdded={load} />
      <BulkUploadDialog open={bulkOpen} onClose={() => setBulkOpen(false)} onDone={load} />
      <NoteDialog lead={noteLead} onClose={() => setNoteLead(null)} onSaved={(updated) => { setLeads((c) => c.map((l) => (l.id === updated.id ? { ...l, ...updated } : l))); setNoteLead(null); }} />
    </div>
  );
}

function NoteDialog({ lead, onClose, onSaved }) {
  const [note, setNote] = useState("");
  const [followUp, setFollowUp] = useState("");
  useEffect(() => { if (lead) { setNote(lead.note || ""); setFollowUp(lead.follow_up || ""); } }, [lead]);

  const save = async () => {
    try {
      await api.patch(`/admin/leads/${lead.id}/note`, { note, follow_up: followUp || null });
      toast.success("Note saved");
      onSaved({ id: lead.id, note, follow_up: followUp || null });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={!!lead} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="lead-note-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Lead Note</DialogTitle>
          <DialogDescription className="text-xs">{lead?.company_name && lead.company_name !== "—" ? lead.company_name : lead?.mobile} · jot a note or set a follow-up date.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-bold">Note</label>
            <Textarea data-testid="lead-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Called, will decide next week…" rows={4} />
          </div>
          <div>
            <label className="text-sm font-bold flex items-center gap-1"><CalendarClock className="h-4 w-4" /> Next follow-up</label>
            <Input data-testid="lead-followup-input" type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} className="h-10" />
          </div>
          <Button data-testid="save-note-btn" onClick={save} className="w-full font-bold h-11">Save Note</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TemplatePlanner({ open, onClose, labels, onChanged }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { if (open) setRows(labels.map((l) => ({ ...l, message: l.message || "" }))); }, [open, labels]);

  const save = async (row) => {
    try { await api.put(`/admin/lead-labels/${row.id}`, { name: row.name, color: row.color, message: row.message }); toast.success("Template saved"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="template-planner-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">WhatsApp Message Templates</DialogTitle>
          <DialogDescription className="text-xs">Set the WhatsApp message used for a lead at each stage. Scroll to find the label. Placeholders: {"{company}"}, {"{mobile}"}, {"{city}"}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[55vh] overflow-y-auto ct-scroll">
          {rows.map((row, idx) => (
            <div key={row.id} data-testid={`template-row-${row.id}`} className="border rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2"><span className="h-3 w-3 rounded-full" style={{ background: row.color }} /><span className="font-heading font-extrabold text-sm">{row.name}</span></div>
              <Textarea data-testid={`template-input-${row.id}`} value={row.message} onChange={(e) => setRows((c) => c.map((r, i) => i === idx ? { ...r, message: e.target.value } : r))} rows={3} placeholder="Hi {company}, …" />
              <div className="flex justify-end mt-2"><Button size="sm" data-testid={`save-template-${row.id}`} onClick={() => save(rows[idx])} className="font-bold">Save</Button></div>
            </div>
          ))}
          {rows.length === 0 && <div className="text-center text-sm text-muted-foreground py-6">No labels yet. Create labels first in Manage Labels.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const PRESET_COLORS = ["#64748b", "#2563eb", "#d97706", "#059669", "#e11d48", "#7c3aed", "#0891b2", "#ca8a04"];

function AddLeadDialog({ open, onClose, labels, onAdded }) {
  const [form, setForm] = useState({ company_name: "", mobile: "", city: "", whatsapp: "", role: "manual", note: "", label_id: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (open) setForm({ company_name: "", mobile: "", city: "", whatsapp: "", role: "manual", note: "", label_id: labels[0]?.id || "" });
  }, [open, labels]);

  const submit = async () => {
    if (!/^\d{10}$/.test(form.mobile.replace(/\D/g, ""))) { toast.error("Enter a valid 10-digit mobile number"); return; }
    setBusy(true);
    try {
      await api.post("/admin/leads", { ...form, role: form.role === "manual" ? "" : form.role });
      toast.success("Lead added");
      onAdded();
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="add-lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Add Lead Manually</DialogTitle>
          <DialogDescription className="text-xs">Capture a lead that came in offline — call, referral or walk-in.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-bold">Company / Name</label>
            <Input data-testid="add-lead-company" value={form.company_name} onChange={set("company_name")} placeholder="e.g. Shiv Tiles" className="h-10" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-bold">Mobile *</label>
              <Input data-testid="add-lead-mobile" value={form.mobile} onChange={set("mobile")} placeholder="10-digit" className="h-10" />
            </div>
            <div>
              <label className="text-sm font-bold">WhatsApp</label>
              <Input data-testid="add-lead-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} placeholder="Defaults to mobile" className="h-10" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-bold">City</label>
              <Input data-testid="add-lead-city" value={form.city} onChange={set("city")} placeholder="e.g. Chennai" className="h-10" />
            </div>
            <div>
              <label className="text-sm font-bold">Type</label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="add-lead-role" className="h-10 font-semibold"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Other / Manual</SelectItem>
                  <SelectItem value="wholesaler">Wholesaler</SelectItem>
                  <SelectItem value="retailer">Retailer</SelectItem>
                  <SelectItem value="manufacturer">Manufacturer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-sm font-bold">Stage</label>
            <Select value={form.label_id} onValueChange={(v) => setForm((f) => ({ ...f, label_id: v }))}>
              <SelectTrigger data-testid="add-lead-stage" className="h-10 font-semibold"><SelectValue placeholder="Select stage" /></SelectTrigger>
              <SelectContent>
                {labels.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-bold">Note</label>
            <Textarea data-testid="add-lead-note" value={form.note} onChange={set("note")} placeholder="Optional context…" rows={2} />
          </div>
          <Button data-testid="add-lead-submit" onClick={submit} disabled={busy} className="w-full font-bold h-11">{busy ? "Adding…" : "Add Lead"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BulkUploadDialog({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  useEffect(() => { if (open) { setFile(null); setResult(null); if (fileRef.current) fileRef.current.value = ""; } }, [open]);

  const downloadSample = () => {
    const sample = "company_name,mobile,whatsapp,city,role,stage,note\nShiv Tiles,9000011122,9000011122,Chennai,wholesaler,New Lead,Walk-in enquiry\nRaj Ceramics,9812345670,,Rajkot,retailer,Interested,Called on 2 June\n";
    const url = URL.createObjectURL(new Blob([sample], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "cleartile-leads-sample.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const upload = async () => {
    if (!file) { toast.error("Choose a CSV file first"); return; }
    setBusy(true); setResult(null);
    try {
      const text = await file.text();
      const { data } = await api.post("/admin/leads/bulk", { csv: text });
      setResult(data);
      toast.success(`${data.added} lead(s) imported`);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="bulk-leads-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Bulk Upload Leads (CSV)</DialogTitle>
          <DialogDescription className="text-xs">Columns: <b>company_name, mobile, whatsapp, city, role, stage, note</b>. Only <b>mobile</b> is required. Duplicate mobiles are skipped.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <button data-testid="download-sample-csv" onClick={downloadSample} className="text-xs font-bold text-primary inline-flex items-center gap-1 hover:underline"><FileDown className="h-3.5 w-3.5" /> Download sample CSV</button>
          <input ref={fileRef} data-testid="bulk-csv-input" type="file" accept=".csv,text/csv" onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
            className="block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-primary file:text-white file:font-bold file:px-4 file:py-2 file:cursor-pointer border rounded-lg p-1.5" />
          {result && (
            <div data-testid="bulk-result" className="text-sm rounded-lg border bg-secondary/40 p-3 space-y-1">
              <div className="font-bold text-emerald-700">✓ {result.added} added</div>
              {result.skipped > 0 && <div className="text-amber-700 font-semibold">⟳ {result.skipped} skipped (duplicates)</div>}
              {result.errors?.length > 0 && <div className="text-rose-600 font-semibold">⚠ {result.errors.length} row error(s): {result.errors.join("; ")}</div>}
            </div>
          )}
          <Button data-testid="bulk-upload-submit" onClick={upload} disabled={busy || !file} className="w-full font-bold h-11 gap-1"><Upload className="h-4 w-4" /> {busy ? "Importing…" : "Import Leads"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function LabelManager({ open, onClose, labels, onChanged }) {
  const [rows, setRows] = useState([]);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(PRESET_COLORS[0]);
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => { if (open) setRows(labels.map((l) => ({ ...l }))); }, [open, labels]);

  const saveRow = async (row) => {
    try { await api.put(`/admin/lead-labels/${row.id}`, { name: row.name, color: row.color, message: row.message || "" }); toast.success("Label saved"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const addLabel = async () => {
    if (!newName.trim()) { toast.error("Enter a label name"); return; }
    try { await api.post("/admin/lead-labels", { name: newName.trim(), color: newColor }); setNewName(""); toast.success("Label created"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const removeLabel = async (id) => {
    if (!window.confirm("Delete this label? Its leads move to the first label.")) return;
    try { await api.delete(`/admin/lead-labels/${id}`); toast.success("Label deleted"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const onDrop = async (idx) => {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); return; }
    const next = [...rows];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setRows(next);
    setDragIdx(null);
    try { await api.put("/admin/lead-labels/reorder", { ids: next.map((r) => r.id) }); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="label-manager-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Manage Labels</DialogTitle>
          <DialogDescription className="text-xs">Drag the handle to reorder stages. Order here sets the column order on the board.</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[50vh] overflow-y-auto ct-scroll">
          {rows.map((row, idx) => (
            <div key={row.id} data-testid={`label-row-${row.id}`}
              draggable onDragStart={() => setDragIdx(idx)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(idx)}
              className={`flex items-center gap-2 border rounded-xl px-2 py-2 bg-card ${dragIdx === idx ? "opacity-50" : ""}`}>
              <span data-testid={`label-drag-${row.id}`} className="cursor-grab active:cursor-grabbing text-muted-foreground touch-none"><GripVertical className="h-5 w-5" /></span>
              <input type="color" data-testid={`label-color-${row.id}`} value={row.color} onChange={(e) => setRows((c) => c.map((r, i) => i === idx ? { ...r, color: e.target.value } : r))} className="h-8 w-9 rounded cursor-pointer border" />
              <Input data-testid={`label-name-${row.id}`} value={row.name} onChange={(e) => setRows((c) => c.map((r, i) => i === idx ? { ...r, name: e.target.value } : r))} className="h-9" />
              <Button size="sm" variant="outline" data-testid={`save-label-${row.id}`} onClick={() => saveRow(rows[idx])}><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" data-testid={`delete-label-${row.id}`} onClick={() => removeLabel(row.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
            </div>
          ))}
        </div>

        <div className="border-t pt-4">
          <div className="text-sm font-bold mb-2">Add a new label</div>
          <div className="flex items-center gap-2">
            <input type="color" data-testid="new-label-color" value={newColor} onChange={(e) => setNewColor(e.target.value)} className="h-9 w-10 rounded cursor-pointer border" />
            <Input data-testid="new-label-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Follow-up" className="h-9" />
            <Button data-testid="add-label-btn" onClick={addLabel} className="font-bold gap-1"><Plus className="h-4 w-4" /> Add</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
